import {
  ALPHA_MAX_STYLE_PICKS,
  type AlphaStylePick,
  type Inspiration,
  inspirationTagZod,
  type MbcBriefAssetFile,
} from "@nlc/shared-types";
import { Check } from "lucide-react";
import { useState } from "react";
import {
  type Selection,
  ToggleButton,
  ToggleButtonGroup,
} from "react-aria-components";

import { useImagePreview } from "@/hooks/useImagePreview";
import { cn } from "@/utils/cn";

import {
  ALPHA_PLAN_COUNT,
  isPicked,
  isPickLimitReached,
  togglePick,
} from "./stylePicks";
import { useInspirationsQuery } from "./useInspirationsQuery";

const ALL = "all";
const MINE = "mine";

type Filter =
  | (typeof inspirationTagZod.options)[number]
  | typeof ALL
  | typeof MINE;

function tagLabel(tag: string): string {
  return tag.charAt(0).toUpperCase() + tag.slice(1);
}

type AlphaStylesStepProps = {
  errorMessage?: null | string;
  onPicksChange: (picks: AlphaStylePick[]) => void;
  picks: AlphaStylePick[];
  uploads: MbcBriefAssetFile[];
};

export function AlphaStylesStep({
  errorMessage,
  onPicksChange,
  picks,
  uploads,
}: AlphaStylesStepProps) {
  const [filter, setFilter] = useState<Filter>(ALL);
  const { data, isError, isPending } = useInspirationsQuery();

  const imageUploads = uploads.filter((file) =>
    (file.mime ?? "").startsWith("image/"),
  );
  const showUploads = filter === ALL || filter === MINE;
  const library =
    filter === MINE
      ? []
      : (data?.inspirations ?? []).filter(
          (inspiration) => filter === ALL || inspiration.tags.includes(filter),
        );
  const limitReached = isPickLimitReached(picks);
  const chips: { label: string; value: Filter }[] = [
    { label: "All", value: ALL },
    ...(imageUploads.length > 0
      ? [{ label: "My inspiration", value: MINE as Filter }]
      : []),
    ...inspirationTagZod.options.map((tag) => ({
      label: tagLabel(tag),
      value: tag as Filter,
    })),
  ];

  const handleFilterChange = (selection: Selection) => {
    const [next] = selection === "all" ? [] : [...selection];

    if (next) setFilter(next as Filter);
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-xl overflow-y-auto p-xl lg:p-3xl">
      <div className="flex items-end justify-between gap-lg">
        <div className="flex max-w-[640px] flex-col gap-md">
          <h1 className="text-display-sm font-extrabold text-text-primary">
            Pick the looks you like.
          </h1>
          <p className="text-lg text-text-tertiary">
            Choose up to {ALPHA_MAX_STYLE_PICKS} styles. Each one shapes its own
            ad, and one more follows your brief as is: {ALPHA_PLAN_COUNT} ads in
            total.
          </p>
        </div>
        <span className="shrink-0 rounded-full bg-bg-brand-primary px-xl py-md text-md font-bold text-text-brand-primary">
          {picks.length} of {ALPHA_MAX_STYLE_PICKS} selected
        </span>
      </div>

      <ToggleButtonGroup
        aria-label="Filter styles"
        className="flex flex-wrap gap-md"
        disallowEmptySelection
        onSelectionChange={handleFilterChange}
        selectedKeys={[filter]}
        selectionMode="single"
      >
        {chips.map((chip) => (
          <ToggleButton
            className={({ isSelected }) =>
              cn(
                "min-h-11 cursor-pointer rounded-full border px-xl py-md text-sm font-semibold outline-hidden focus-visible:ring-2 focus-visible:ring-border-brand",
                isSelected
                  ? "border-bg-primary-solid bg-bg-primary-solid text-text-white"
                  : "border-border-primary bg-bg-primary text-text-secondary",
              )
            }
            id={chip.value}
            key={chip.value}
          >
            {chip.label}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>

      <ul className="grid grid-cols-2 gap-lg md:grid-cols-3 xl:grid-cols-4">
        {showUploads
          ? imageUploads.map((file) => {
              const pick: AlphaStylePick = {
                attachmentId: file.attachmentId,
                kind: "upload",
              };
              const selected = isPicked(picks, pick);

              return (
                <StyleTile
                  badge="Yours"
                  isDisabled={limitReached && !selected}
                  isSelected={selected}
                  key={`upload-${file.attachmentId}`}
                  label={`Your inspiration ${file.name}`}
                  onToggle={() => onPicksChange(togglePick(picks, pick))}
                  url={file.url}
                />
              );
            })
          : null}
        {library.map((inspiration: Inspiration) => {
          const pick: AlphaStylePick = {
            inspirationId: inspiration.id,
            kind: "inspiration",
          };
          const selected = isPicked(picks, pick);

          return (
            <StyleTile
              isDisabled={limitReached && !selected}
              isSelected={selected}
              key={inspiration.id}
              label={`Inspiration ${inspiration.id}`}
              onToggle={() => onPicksChange(togglePick(picks, pick))}
              url={inspiration.url}
            />
          );
        })}
      </ul>

      {isPending ? (
        <p className="text-sm text-text-tertiary">Loading styles…</p>
      ) : null}
      {isError ? (
        <p className="text-sm text-text-tertiary">
          Couldn&apos;t load the style library. You can still generate without
          picking.
        </p>
      ) : null}
      {errorMessage ? (
        <p className="text-sm text-text-error-primary" role="alert">
          {errorMessage}
        </p>
      ) : null}
    </div>
  );
}

function StyleTile({
  badge,
  isDisabled,
  isSelected,
  label,
  onToggle,
  url,
}: {
  badge?: string;
  isDisabled: boolean;
  isSelected: boolean;
  label: string;
  onToggle: () => void;
  url: string;
}) {
  const { hasFailed, onError, src } = useImagePreview({ url });

  return (
    <li>
      <ToggleButton
        aria-label={label}
        className={({ isDisabled: disabled, isSelected: selected }) =>
          cn(
            "relative block w-full cursor-pointer overflow-hidden rounded-xl border p-md outline-hidden focus-visible:ring-2 focus-visible:ring-border-brand",
            selected
              ? "border-2 border-border-brand-solid bg-bg-brand-primary"
              : "border-border-secondary bg-bg-primary",
            disabled && "cursor-not-allowed opacity-50",
          )
        }
        isDisabled={isDisabled}
        isSelected={isSelected}
        onChange={onToggle}
      >
        {hasFailed ? (
          <div className="aspect-square w-full rounded-lg bg-bg-secondary" />
        ) : (
          <img
            alt=""
            className="aspect-square w-full rounded-lg object-cover"
            loading="lazy"
            onError={onError}
            src={src}
          />
        )}
        {badge ? (
          <span className="absolute top-lg left-lg rounded-full bg-bg-primary px-md py-xs text-xs font-bold text-text-brand-primary shadow-sm">
            {badge}
          </span>
        ) : null}
        {isSelected ? (
          <span className="absolute top-lg right-lg grid size-8 place-items-center rounded-full bg-bg-brand-solid text-text-white">
            <Check className="size-4" />
          </span>
        ) : null}
      </ToggleButton>
    </li>
  );
}
